import React, { memo, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { SidebarBoundary } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/ui/sidebar-boundary/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useSidebarNav4 } from '@jira/navigation-apps__sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import { MENU_ID_ROADMAPS } from '@jira/navigation-apps__sidebar-nav4-context/src/common/constants.tsx';
import type { Nav4DragAndDropMenuItemProps } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { PACKAGE_NAME, TEAM_NAME } from './common/constants.tsx';
import { ActionButtons } from './ActionButtons.tsx';
import { RoadmapsProviders } from './ui/roadmaps/roadmaps-providers/index.tsx';
import { Nav4RoadmapsDefault } from './Nav4RoadmapsDefault.tsx';
import { RoadmapNavExpanded } from './RoadmapNavExpanded.tsx';
import type { Nav4RoadmapsWithRecent_query$key } from './__generated__/Nav4RoadmapsWithRecent_query.graphql.ts';

export type Nav4RoadmapsProps = Nav4DragAndDropMenuItemProps & {
	isExpanded: boolean;
	queryRefFragment: Nav4RoadmapsWithRecent_query$key;
};

export const Nav4RoadmapsWithRecent: React.FC<Nav4RoadmapsProps> = memo(
	(props: Nav4RoadmapsProps) => {
		const { isExpanded, queryRefFragment, ...dndProps } = props;
		const roadmapNavigationQuery = useFragment<Nav4RoadmapsWithRecent_query$key>(
			graphql`
				fragment Nav4RoadmapsWithRecent_query on Query
				@argumentDefinitions(currentURL: { type: "URL" }) {
					...RoadmapNavExpanded_query @arguments(currentURL: $currentURL)
				}
			`,
			queryRefFragment,
		);

		const actionButtons = useMemo(() => <ActionButtons />, []);

		const { isSelectedPath } = useSidebarNav4();
		const isRoadmapSelectedPath = isSelectedPath(MENU_ID_ROADMAPS);
		const isExpandedValue = fg('navx-global-side-nav')
			? !!dndProps.isExpandOrFlyoutOpen && isExpanded
			: isExpanded;

		return (
			<SidebarBoundary
				id="nav4.sidebar.roadmaps"
				packageName={PACKAGE_NAME}
				teamName={TEAM_NAME}
				ufoName="nav4-sidebar-roadmaps"
			>
				<RoadmapsProviders>
					{isRoadmapSelectedPath && roadmapNavigationQuery ? (
						<RoadmapNavExpanded
							actionsOnHover={actionButtons}
							isExpanded={isExpandedValue}
							queryRefFragment={roadmapNavigationQuery}
							{...dndProps}
						/>
					) : (
						<Nav4RoadmapsDefault
							actionsOnHover={actionButtons}
							isExpanded={isExpandedValue}
							{...dndProps}
						/>
					)}
				</RoadmapsProviders>
			</SidebarBoundary>
		);
	},
);
